import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';

const TripTracker = ({ booking, onClose, onTripUpdate, isLocked = false }) => {
  const { user } = useAuth();
  const [tripStatus, setTripStatus] = useState(booking?.status || 'confirmed');
  const [driverStatus, setDriverStatus] = useState(booking?.driver_status || null); // 'on_way' | 'arrived'
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isActive, setIsActive] = useState(booking?.status === 'in_progress');
  const [loading, setLoading] = useState(false);
  
  // Completion form state
  const [actualDuration, setActualDuration] = useState('');
  const [actualDistance, setActualDistance] = useState('');
  const [driverNotes, setDriverNotes] = useState('');
  const [tripModifications, setTripModifications] = useState('');
  const [showCompletionForm, setShowCompletionForm] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Initialize trip status for in_progress trips
  useEffect(() => {
    if (booking?.status === 'in_progress') {
      // Immediately fetch current trip status
      const fetchTripStatus = async () => {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/driver/booking/${booking.id}/trip-status?driver_id=${user.id}`
          );
          
          if (response.ok) {
            const statusData = await response.json();
            setElapsedTime(statusData.elapsed_minutes || 0);
            setTripStatus(statusData.status);
            setIsActive(statusData.is_active || false);
          }
        } catch (error) {
          console.error('Failed to fetch initial trip status:', error);
        }
      };
      
      fetchTripStatus();
    }
  }, [booking?.id, booking?.status, user.id, BACKEND_URL]);

  // Real-time trip status polling
  useEffect(() => {
    let interval;
    
    if (tripStatus === 'in_progress' && isActive) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/driver/booking/${booking.id}/trip-status?driver_id=${user.id}`
          );
          
          if (response.ok) {
            const statusData = await response.json();
            setElapsedTime(statusData.elapsed_minutes || 0);
            setTripStatus(statusData.status);
            setIsActive(statusData.is_active || false);
          }
        } catch (error) {
          console.error('Failed to fetch trip status:', error);
        }
      }, 1000); // Update every second
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tripStatus, isActive, booking.id, user.id, BACKEND_URL]);

  const handleClose = () => {
    if (!isLocked) {
      document.body.style.overflow = 'unset';
      onClose();
    }
  };

  const handleOnWay = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/driver/booking/${booking.id}/on-way?driver_id=${user.id}`, { method: 'PUT' });
      if (res.ok) {
        setDriverStatus('on_way');
        alert('Rider notified — you are on the way!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update status');
      }
    } catch { alert('Network error'); }
    finally { setLoading(false); }
  };

  const handleArrived = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/driver/booking/${booking.id}/arrived?driver_id=${user.id}`, { method: 'PUT' });
      if (res.ok) {
        setDriverStatus('arrived');
        alert('Rider notified — you have arrived!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update status');
      }
    } catch { alert('Network error'); }
    finally { setLoading(false); }
  };

  const handleStartTrip = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/driver/booking/${booking.id}/start?driver_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driver_notes: driverNotes
        })
      });

      if (response.ok) {
        await response.json();
        setTripStatus('in_progress');
        setIsActive(true);
        setElapsedTime(0);
        alert('Trip started successfully! Timer is now running.');
        onTripUpdate && onTripUpdate();
      } else {
        const error = await response.json();
        alert(`Failed to start trip: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error starting trip:', error);
      alert('Failed to start trip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEndTrip = () => {
    setShowCompletionForm(true);
  };

  const handleCompleteTrip = async () => {
    try {
      setLoading(true);
      
      const completionData = {
        driver_trip_notes: driverNotes,
        trip_modifications: tripModifications
      };

      // Add actual duration if specified, otherwise use elapsed time
      if (actualDuration) {
        completionData.actual_duration_minutes = parseInt(actualDuration);
      }

      // Add actual distance if specified
      if (actualDistance) {
        completionData.actual_distance_km = parseFloat(actualDistance);
      }

      const response = await fetch(`${BACKEND_URL}/api/driver/booking/${booking.id}/complete-with-actuals?driver_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completionData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Trip completed successfully!\n\nOriginal Fare: R${result.trip_summary.estimated_fare}\nAdjusted Fare: R${result.trip_summary.actual_fare} (${(result.trip_summary.pricing_adjustment * 100).toFixed(1)}% of estimate)\nYour Earnings: R${result.trip_summary.driver_earnings} (70% of adjusted fare)\nCompany Commission: R${result.trip_summary.company_commission} (30% of adjusted fare)`);
        setTripStatus('completed');
        setIsActive(false);
        onTripUpdate && onTripUpdate();
        handleClose();
      } else {
        const error = await response.json();
        alert(`Failed to complete trip: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error completing trip:', error);
      alert('Failed to complete trip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return createPortal(
    <div className="modal-overlay" onClick={isLocked ? null : handleClose}>
      <div className="modal-content trip-tracker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🚗 Trip Tracker</h2>
          {isLocked && <span className="locked-badge">🔒 ACTIVE TRIP</span>}
          {!isLocked && <button className="modal-close" onClick={handleClose}>×</button>}
        </div>

        <div className="modal-body">
          <div className="trip-info">
            <h3>{booking.passengerName}</h3>
            <p><strong>From:</strong> {booking.pickupAddress}</p>
            <p><strong>To:</strong> {booking.destinationAddress}</p>
            <p><strong>Status:</strong> <span className={`status-${tripStatus}`}>{tripStatus.replace('_', ' ').toUpperCase()}</span></p>
          </div>

          {tripStatus === 'confirmed' && (
            <div className="trip-start-section">
              <div className="estimated-info">
                <h4>Estimated Trip Details:</h4>
                <p>⏱️ Duration: {booking.companionMinutes || booking.quoteDetails?.totalCompanionMinutes || 0} minutes</p>
                <p>📏 Distance: {booking.quoteDetails?.totalDistance || booking.quoteDetails?.distance || 0}km</p>
                <p>💰 Fare: R{booking.quoteDetails?.totalCost || booking.quoteDetails?.totalFare || 0}</p>
              </div>

              {/* Driver status buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                  className="btn-secondary"
                  onClick={handleOnWay}
                  disabled={loading || driverStatus === 'on_way' || driverStatus === 'arrived'}
                  data-testid="on-way-btn"
                  style={{ flex: 1 }}
                >
                  {driverStatus === 'on_way' || driverStatus === 'arrived' ? '✓ On My Way (Sent)' : '🚗 On My Way'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={handleArrived}
                  disabled={loading || driverStatus === 'arrived' || !driverStatus}
                  data-testid="arrived-btn"
                  style={{ flex: 1 }}
                >
                  {driverStatus === 'arrived' ? '✓ Arrived (Sent)' : '📍 Arrived at Pickup'}
                </button>
              </div>
              
              <div className="driver-notes-input">
                <label htmlFor="start-notes">Pre-trip Notes (Optional):</label>
                <textarea
                  id="start-notes"
                  value={driverNotes}
                  onChange={(e) => setDriverNotes(e.target.value)}
                  placeholder="Any notes before starting the trip..."
                  rows="3"
                />
              </div>

              <button 
                className="btn-primary start-trip-btn"
                onClick={handleStartTrip}
                disabled={loading}
              >
                {loading ? 'Starting...' : '🚀 Start Trip'}
              </button>
            </div>
          )}

          {tripStatus === 'in_progress' && (
            <div className="trip-progress-section">
              <div className="trip-timer">
                <div className="timer-display">
                  <span className="timer-label">Trip Time:</span>
                  <span className="timer-value">{formatTime(elapsedTime)}</span>
                </div>
                <div className="timer-status">
                  <span className="status-indicator active"></span>
                  <span>Trip in Progress</span>
                </div>
              </div>

              <div className="trip-comparison">
                <div className="comparison-item">
                  <span>Estimated: {formatTime(booking.companionMinutes || booking.quoteDetails?.totalCompanionMinutes || 0)}</span>
                  <span>Actual: {formatTime(elapsedTime)}</span>
                </div>
              </div>

              <button 
                className="btn-secondary end-trip-btn"
                onClick={handleEndTrip}
                disabled={loading}
              >
                🏁 End Trip
              </button>
            </div>
          )}

          {showCompletionForm && (
            <div className="trip-completion-form">
              <h4>Complete Trip - Final Details</h4>
              
              <div className="completion-inputs">
                <div className="input-group">
                  <label htmlFor="actual-duration">Actual Trip Time (minutes):</label>
                  <input
                    type="number"
                    id="actual-duration"
                    value={actualDuration}
                    onChange={(e) => setActualDuration(e.target.value)}
                    placeholder={`Current: ${elapsedTime} minutes`}
                    min="1"
                  />
                  <small>Leave empty to use timer: {formatTime(elapsedTime)}</small>
                </div>

                <div className="input-group">
                  <label htmlFor="actual-distance">Actual Distance (km):</label>
                  <input
                    type="number"
                    id="actual-distance"
                    value={actualDistance}
                    onChange={(e) => setActualDistance(e.target.value)}
                    placeholder={`Estimated: ${booking.quoteDetails?.totalDistance || booking.quoteDetails?.distance || 0}km`}
                    min="0.1"
                    step="0.1"
                  />
                  <small>Edit if actual distance differs from estimated</small>
                </div>

                <div className="input-group">
                  <label htmlFor="trip-notes">Trip Notes:</label>
                  <textarea
                    id="trip-notes"
                    value={driverNotes}
                    onChange={(e) => setDriverNotes(e.target.value)}
                    placeholder="Any notes about the trip..."
                    rows="3"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="trip-modifications">Trip Modifications:</label>
                  <textarea
                    id="trip-modifications"
                    value={tripModifications}
                    onChange={(e) => setTripModifications(e.target.value)}
                    placeholder="Any changes made during the trip (route changes, extra stops, etc.)"
                    rows="3"
                  />
                </div>
              </div>

              <div className="completion-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowCompletionForm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleCompleteTrip}
                  disabled={loading}
                >
                  {loading ? 'Completing...' : '✅ Complete Trip'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TripTracker;